<!-- eslint-disable no-use-before-define -->
<script setup lang="ts">
import { useStore } from 'vuex'
import ReportsTabs, { type actionID } from '@/components/hhv3/Analyzer/ReportsTabs.vue'
import Report from '@/components/hhv3/Analyzer/Report.vue'
import { onMounted, ref, watchEffect, computed } from 'vue'
import NewReport from '@/components/hhv3/Analyzer/NewReport/NewReport.vue'
import { usePaginatedFetcher } from '@/composables/fetchPaginated'
import AnalyzeBlogMessage from '@/views/analyze/original/AnalyzeBlogMessage.vue'
import { DISABLED_OLD_ANALYZER_REPORT_COLUMNS } from '@/types/api/reports'
import type {
  ActiveAnalyzerReport,
  AnalyzerReport,
  ReportParams,
  Filter,
  FilterCategory,
  FilterValue,
  DisabledColumnFieldName,
} from '@/types/api/reports'
import { useFetch, getFetchCallBase } from '@/composables/fetch'
import type { ApiError, ID } from '@/types/basic'
import * as R from 'remeda'
import { CALL_DEBOUNCE_MS } from '@/helpers/constantsNew'
import { useRouter } from 'vue-router'
import { t } from '@/locales/i18n'

type AnalyzerError = ApiError | undefined

let reportsLimit: number | undefined
let reportsCount = 0

const activeReport = ref({} as ActiveAnalyzerReport)
const isCreateNew = ref(false)
const blockDefaultReportCreate = ref(false)
const newReportRef = ref<InstanceType<typeof NewReport> | null>(null)
const analyzerError = ref(undefined as AnalyzerError)
const filterCategories = ref([] as FilterCategory[])

const { fetch } = useFetch()
const store = useStore()
const router = useRouter()

const updateCount = (count: number) => {
  reportsCount = count
}

const initialLoading = [...new Array(100).keys()].map(i => ({
  created_at: '2022-02-22T14:00:00.000000Z',
  id: `${i}`,
  is_active: false,
  is_opened: false,
  is_saved: false,
  name: 'loading...',
  ordering_position: 0,
  selected_view: 'TABLE' as const,
  tags: [],
}))

const { fetchData, fetchNextData, fetchPreviousData, state } = usePaginatedFetcher<
  AnalyzerReport,
  ReportParams
>('v2', 'poker/hand-history/reports/', initialLoading, 'initial', updateCount)

const fetchReportsTemplatesCall = ref(
  getFetchCallBase({
    apiVersion: 'v2',
    endpoint: 'poker/hand-history/reports/templates/',
  }),
)

const duplicateReportCall = ref(
  getFetchCallBase({
    apiVersion: 'v2',
    method: 'post',
  }),
)

const fetchActiveReportCall = ref(
  getFetchCallBase({
    apiVersion: 'v2',
    endpoint: 'poker/hand-history/reports/active/',
  }),
)

const updateReportCall = ref(
  getFetchCallBase({
    apiVersion: 'v2',
    method: 'patch',
  }),
)

const reportConfCall = ref(
  getFetchCallBase({
    apiVersion: 'v2',
    endpoint: 'poker/hand-history/filters/',
  }),
)

const getReportConf = async () => {
  const { error, data } = await fetch(reportConfCall.value)

  if (error) {
    analyzerError.value = error
    return
  }

  filterCategories.value = data.categories
}

const updateReport = async (
  updatedId: ID = 'active',
  params: Partial<ActiveAnalyzerReport>,
  options?: { disableReactiveCall?: true },
) => {
  updateReportCall.value.conf.endpoint = `poker/hand-history/reports/${updatedId}/`
  updateReportCall.value.data = params

  const { data, error } = await fetch(
    options?.disableReactiveCall ? { ...updateReportCall.value } : updateReportCall.value,
  )

  if (error) {
    analyzerError.value = error
    return undefined
  }

  return data
}

const duplicateReport = async (
  report: AnalyzerReport,
): Promise<undefined | ActiveAnalyzerReport> => {
  duplicateReportCall.value.conf.endpoint = `poker/hand-history/reports/${report.id}/copy/`
  duplicateReportCall.value.data = { is_saved: true, name: `${report.name} (copy)` }

  const { data, error } = await fetch(duplicateReportCall.value)

  if (error) {
    analyzerError.value = error
    return undefined
  }

  if (newReportRef.value) newReportRef.value.fetchInitial()

  if (!state.data.length || data.ordering_position > R.last(state.data)!.ordering_position) {
    state.data = [...state.data, data]
  } else {
    const index = state.data.findIndex(
      ({ ordering_position }) => ordering_position > data.ordering_position,
    )

    state.data = [
      ...state.data.slice(0, index),
      data,
      ...state.data.slice(index).map(i => ({ ...i, ordering_position: i.ordering_position + 1 })),
    ]
  }
  reportsCount++

  return data
}

const createNewReport = () => {
  if (isCreateNew.value) return
  isCreateNew.value = true
  activeReport.value = {} as ActiveAnalyzerReport
}

type DuplicateParams = {
  is_active?: boolean
  is_saved?: boolean
  is_opened?: boolean
  ordering_position?: number
  name?: string
}

const createTemplateReport = async (params?: DuplicateParams) => {
  const { data: templatesData, error: templatesError } = await fetch(
    fetchReportsTemplatesCall.value,
  )

  if (templatesError) {
    analyzerError.value = templatesError
    return undefined
  }

  const reportId = templatesData.results[0].id

  duplicateReportCall.value.conf.endpoint = `poker/hand-history/reports/templates/${reportId}/copy/`
  if (params) duplicateReportCall.value.data = params

  const { data, error } = await fetch(duplicateReportCall.value)

  if (error) {
    analyzerError.value = error
    return undefined
  }
  reportsCount++
  return data
}

const createNewDefaultReport = async (
  filters: Filter[] = [],
  reportName: string = t('hhv3.new_report'),
) => {
  state.loading = 'initial'
  reportConfCall.value.loading = true
  await handleCreateReport(reportName)

  if (filters.length)
    await updateFilters(
      R.uniqueBy([...filters, ...activeReport.value.saved_filters], ({ filter }) => filter.id),
    )
  reportConfCall.value.loading = false
}

const createNewDefaultReportFromQuery = async () => {
  state.loading = 'initial'
  blockDefaultReportCreate.value = true // block further creation of default report & adding filters
  const filters = router.currentRoute.value.query

  const reportName =
    typeof filters.reportName === 'string'
      ? filters.reportName
      : // only name Poker Wizard Hands for now
        filters?.source === 'POKER_WIZARD'
        ? 'Play Mode Hands'
        : ''

  const filtersToApply = Object.keys(filters)
    .map(key => {
      const filter = filterCategories.value
        .flatMap(category => category.filters)
        .find(f => f.field_name === key)
      if (!filter) return null
      const value = (filters[key] as FilterValue) || ''
      const api_filter = filter.api_filters[0]
      return {
        is_active: true,
        value:
          api_filter.filter_type === 'MULTIPLE_CHOICE' && !Array.isArray(value) ? [value] : value,
        filter,
        metadata: { display_value: value },
      } as Filter
    })
    .filter(Boolean) as Filter[]

  await createNewDefaultReport(filtersToApply, reportName)

  router.replace({ query: {} })
}

const fetchActiveReportById = async (reportId: string) => {
  const { data, error } = await fetch(
    getFetchCallBase({
      apiVersion: 'v2',
      endpoint: `poker/hand-history/reports/${reportId}/`,
    }),
  )

  if (error) {
    return fetchActiveReport()
  }

  if (!data.is_opened || !data.is_active) {
    const newActiveReport = await updateReport(reportId, { is_active: true, is_opened: true })
    activeReport.value = newActiveReport
    return newActiveReport
  }

  activeReport.value = data

  return data
}

const fetchActiveReport = async () => {
  const { data, error } = await fetch(fetchActiveReportCall.value)

  let newActiveReport = data
  if (error || !data?.is_opened) {
    newActiveReport = await createTemplateReport()
  }
  activeReport.value = newActiveReport ?? {}

  const isStatsView = router.currentRoute.value.query.view === 'stats'

  if (isStatsView) {
    activeReport.value = {
      ...activeReport.value,
      selected_view: 'STATS',
    }
  }

  return newActiveReport
}

const initialReportsFetch = async () => {
  isCreateNew.value = false

  const reportIdFromSearchQuery =
    typeof router.currentRoute.value.query.reportId === 'string'
      ? router.currentRoute.value.query.reportId
      : undefined

  const newActiveReport = reportIdFromSearchQuery
    ? await fetchActiveReportById(reportIdFromSearchQuery)
    : await fetchActiveReport()

  if (!newActiveReport) return

  const offset = getTabsOffset(newActiveReport.ordering_position, reportsLimit)
  await fetchData({
    offset,
    initial: true,
    limit: reportsLimit,
    params: { is_opened: true },
  })

  if ('new_report' in router.currentRoute.value.query && !blockDefaultReportCreate.value)
    await createNewDefaultReportFromQuery()
}

const handleFetchPrevious = async () => {
  if (!state.loading) {
    await fetchPreviousData()
    // TODO after succes jump to previous fisrt tab in the list so it seems seamless (currentFirstTab)
    // hard to do now - we will have to do a bit of rewriting and use near infinite master scrollcontainer filled with limit/offset blocks
    // that will load their tabs whe this block gets into view
  }
}

const setActiveReport = async (id: ID) => {
  const newActiveReport = await updateReport(id, { is_active: true, is_opened: true })

  if (state.data.some(report => report.id === id)) {
    activeReport.value = newActiveReport ?? {}
    isCreateNew.value = false
  } else initialReportsFetch()

  router.replace({ query: {} })
}
const handleCreateReport = async (name?: string) => {
  await createTemplateReport({
    ordering_position: reportsCount + 1,
    is_saved: true,
    is_active: true,
    is_opened: true,
    ...(name && { name }),
  })

  await initialReportsFetch()
  isCreateNew.value = false
}

const getTabsOffset = (currentPosition, limit) =>
  Math.max(-1 + currentPosition - Math.floor((limit || 0) / 2), 0)

const updateReportsLimit = (count: number) => {
  if (!R.isNumber(reportsLimit)) {
    reportsLimit = count
    initialReportsFetch()
    return
  }

  if (R.isEmpty(activeReport.value)) return
  if ((reportsLimit || 0) >= count) return
  if (!state.nextOffset) return

  reportsLimit = count

  const offset = getTabsOffset(activeReport.value.ordering_position, reportsLimit)
  fetchData({
    offset,
    initial: true,
  })
}

const handleUpdateReport = async (actionId: actionID, report: AnalyzerReport) => {
  const index = state.data.findIndex(({ id }) => id === report.id)
  const nextActiveId = state.data[index + 1]?.id || state.data[index - 1]?.id

  switch (actionId) {
    case 'rename_report':
      state.data[index].name = report.name
      await updateReport(report.id, { name: report.name })
      if (report.is_saved && newReportRef.value) newReportRef.value.fetchInitial()
      break

    case 'duplicate_report':
      // TODO add loader?
      duplicateReport(report)
      break

    case 'close_report':
      state.data = state.data.filter(({ id }) => id !== report.id)

      if (report.id === activeReport.value.id) await setActiveReport(nextActiveId)

      updateReport(report.id, { is_opened: false, is_active: false }, { disableReactiveCall: true })
      reportsCount--
      break

    case 'close_all_other_reports':
      state.data = state.data.filter(({ id }) => id === report.id)

      closeAllOtherReports(report.id)
      reportsCount = 1
      break

    default:
      break
  }
}

const closeAllOtherReports = async (id: ID) => {
  const call = getFetchCallBase({
    method: 'post',
    apiVersion: 'v2',
    endpoint: `poker/hand-history/reports/${id}/close-others/`,
  })
  const { error } = await fetch(call)
  if (error) {
    analyzerError.value = error
  }
}

const patchUpdateReport = async (params: Partial<ActiveAnalyzerReport>) => {
  const call = getFetchCallBase({
    method: 'patch',
    apiVersion: 'v2',
    endpoint: `poker/hand-history/reports/${activeReport.value.id}/`,
  })
  call.data = params
  const { error } = await fetch(call)
  if (error) {
    analyzerError.value = error
  }
}

const updateColumns = columns => {
  activeReport.value.saved_columns = columns
  patchUpdateReport({ saved_columns: columns })
}
const debouncedUpdateFilterCall = R.debounce(
  filters => patchUpdateReport({ saved_filters: filters }),
  { waitMs: CALL_DEBOUNCE_MS },
)
const updateFilters = async (filters: Filter[]) => {
  activeReport.value.saved_filters = filters
  debouncedUpdateFilterCall.call(filters)
}

const handleSwitchView = (_e: MouseEvent, view: AnalyzerReport['selected_view']) => {
  activeReport.value.selected_view = view
  updateReport(activeReport.value.id, { selected_view: view })
}

const filteredActiveReport = computed(() => ({
  ...activeReport.value,
  saved_columns:
    activeReport.value.saved_columns?.filter(
      column => !R.isIncludedIn(column?.column?.field_name, DISABLED_OLD_ANALYZER_REPORT_COLUMNS),
    ) || [],
}))

onMounted(() => {
  getReportConf()
})

watchEffect(() => {
  if (analyzerError.value && analyzerError.value?.code !== 'ERR_CANCELED') {
    store.dispatch('messages', {
      type: 'error',
      error: analyzerError.value,
    })
    analyzerError.value = undefined
  }
})
</script>
<template>
  <AnalyzeBlogMessage class="mb-3" />

  <div class="f-column flex-1 mh-0 mw-0">
    <ReportsTabs
      style="margin: 0 0.5rem"
      :loadingVariant="state.loading"
      :activeReport="filteredActiveReport"
      :reports="state.data"
      :isCreateNew
      @update:report="handleUpdateReport"
      @fetchPrevious:reports="handleFetchPrevious"
      @fetchNext:reports="
        () => {
          if (!state.loading) fetchNextData()
        }
      "
      @setActive:report="setActiveReport"
      @create:new="createNewReport"
      @update:reportsLimit="updateReportsLimit"
      @update:reports="newReports => (state.data = newReports)"
      @patch:report="(id, params) => updateReport(id, params)"
    />
    <NewReport
      v-if="isCreateNew"
      ref="newReportRef"
      @setActive:report="setActiveReport"
      @create:report="handleCreateReport"
      @delete:reports="initialReportsFetch"
      @setError="analyzerError = $event"
    />
    <Report
      v-else
      :filterCategories
      :loading="reportConfCall.loading"
      :report="filteredActiveReport"
      class="position-relative"
      @update:columns="updateColumns"
      @update:filters="updateFilters"
      @new:filters="createNewDefaultReport"
      @click:view="handleSwitchView"
    >
    </Report>
  </div>
</template>
